import {
  GetShipmentsQuery,
  RoutingJobsQuery,
  ShipmentEdge,
  ShipmentsSorting,
  useGetShipmentsForSalesReportLazyQuery,
  useGetShipmentsLazyQuery,
  useRoutingJobsLazyQuery,
} from "@api/graphql/generated/generated-types";
import React, { createContext, useContext, useEffect, useState } from "react";
import { SortFieldsType, useTableControl } from "./TableControlContext";
import { useDebounce, useLocalStorage } from "usehooks-ts";
import { makeGetShipmentsVariable } from "../utils/makeGetShipmentsVariable";
import { makeShipmentFilterVariables } from "../utils/makeShipmentFilterVariables";
import {
  ShipmentsOverviewCounts,
  useShipmentCounts,
} from "../hooks/useShipmentCounts";
import { useShipmentFilterSnapshot } from "../hooks/useShipmentFilterSnapshot";
import { AuthContext } from "@src/auth/components/AuthProvider";
import { useShipmentFiltersContext } from "./ShipmentFiltersContext";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useCourieStore } from "@src/common/lib/store";
import { ShipmentRow } from "../types";
import { makeShipmentEdgesToShipmentRow } from "../utils/shipmentRowFormatter";
import { useRouter } from "next/router";

// Define the context interface
interface ShipmentsCoreDataContextProps {
  shipmentsApiResponse: GetShipmentsQuery | undefined;
  refetchGetShipments: any;
  shipmentsApiLoading: boolean;
  rows: ShipmentRow[];
  onDispatch: (lastChangedShipmentId?: string | null | undefined) => void;
  fetchShipments: () => void;
  indexToRows: object;
  shipmentIdToIndex: object;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  limit: { id: string; label: string };
  setLimit: (label: { id: string; label: string }) => void;
  routingJobsData: RoutingJobsQuery | undefined;
  routingJobsLoading: boolean;
  shipmentsOverviewCounts: ShipmentsOverviewCounts | null;
  refetchRoutingJobs: any;
}

// the context with a default value
const ShipmentsCoreDataContext =
  createContext<ShipmentsCoreDataContextProps | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

const DEBOUNCE_TIME = 700;

const omitUndefined = <T extends object>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Partial<T>;

// the context provider
const ShipmentsCoreDataProvider = ({ children }: Props) => {
  // contexts
  const { showToast } = useCourieStore();
  const { courieUser, courierId, employeeId } = useContext(AuthContext);
  const { sortFields, setSortFields } = useTableControl();
  const {
    minDate,
    maxDate,
    shipmentDateRangeStart,
    shipmentDateRangeEnd,
    completedDateRangeStart,
    completedDateRangeEnd,
    selectedPrimaryFilter,
    selectedDriverIdFilter,
    selectedCustomer,
    trackingNumbersFilter,
    displayIdsFilter,
    orderNameSearch,
    hasOrderInternalNote,
    hasDriverNoteOnly,
    hasWaitTimeOrOverride,
    hasMultipleDriverPayOnly,
    hasUnapprovedDriverPackageReportOnly,
    isLate,
    isOnTime,
    selectedCustomFilter,
    selectedDriverLabelIds,
    selectedOrderLabelIds,
    selectedEndCustomerLabelIds,
    selectedCourierBrandIds,
    selectedAcquisitionTypes,
    selectedRecurrenceTypes,
    isAttempted,
    selectedPickupZoneIds,
    selectedDropoffZoneIds,
    selectedPackageTypeIds,
    selectedOrderVehicleTypeIds,
    selectedDriverVehicleTypeIds,
    selectedServiceTypeIds,
    minWeightInPounds,
    maxWeightInPounds,
    selectedRouteTemplateIds,
  } = useShipmentFiltersContext();
  const router = useRouter();
  const filterSnapshot = useShipmentFilterSnapshot();
  // states
  const [rows, setRows] = useState<ShipmentRow[]>([]);
  const [indexToRows, setIndexToRows] = useState<object>({});
  const [shipmentIdToIndex, setShipmentIdToIndex] = useState<object>({});
  const debouncedSortFields = useDebounce<SortFieldsType>(
    sortFields,
    DEBOUNCE_TIME
  );
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [limit, setLimit] = useLocalStorage("ShipmentsTableRowsPerPage", {
    id: "100",
    label: "100",
  });
  const memoizedLimit = React.useMemo(() => limit, [JSON.stringify(limit)]);
  const memoizedCurrentPage = React.useMemo(() => currentPage, [currentPage]);
  // queries
  const [
    getShipmentsForSalesReport,
    {
      data: shipmentsForSalesReportData,
      loading: shipmentsForSalesReportLoading,
      error: shipmentsForSalesReportError,
      refetch: refetchShipmentsForSalesReport,
    },
  ] = useGetShipmentsForSalesReportLazyQuery();
  const [
    getRoutingJobs,
    {
      data: routingJobsData,
      loading: routingJobsLoading,
      error: routingJobsError,
      refetch: refetchRoutingJobs,
    },
  ] = useRoutingJobsLazyQuery({
    pollInterval: 10000,
  });
  const [
    getShipments,
    {
      data: shipmentsApiResponse,
      loading: shipmentsApiLoading,
      error: shipmentsApiError,
      refetch: refetchGetShipments,
    },
  ] = useGetShipmentsLazyQuery({
    fetchPolicy: "no-cache",
    pollInterval: 10 * 1000,
  });

  // memoized value to combine both responses
  const combinedShipmentsResponse = React.useMemo(() => {
    const shouldUseSalesReport = router.pathname.includes("/office/sales");
    return shouldUseSalesReport
      ? shipmentsForSalesReportData
      : shipmentsApiResponse;
  }, [shipmentsForSalesReportData, shipmentsApiResponse, router.pathname]);

  const shipmentsOverviewCounts = useShipmentCounts(combinedShipmentsResponse);

  // memoized loading state
  const isLoading = React.useMemo(() => {
    return shipmentsApiLoading || shipmentsForSalesReportLoading;
  }, [shipmentsApiLoading, shipmentsForSalesReportLoading]);

  // effects

  useEffect(() => {
    if (courierId) {
      getRoutingJobs({
        variables: {
          courierId,
          start: 0,
          limit: 1,
        },
      });
    }
  }, [courierId, shipmentsApiResponse]);

  useEffect(() => {
    setRows([]);
    setIndexToRows({});
    setShipmentIdToIndex({});
    if (
      combinedShipmentsResponse &&
      combinedShipmentsResponse.shipments?.edges
    ) {
      const shipmentRows: ShipmentRow[] = makeShipmentEdgesToShipmentRow(
        combinedShipmentsResponse.shipments.edges as ShipmentEdge[]
      );
      const indexToRowsObj: object = {};
      const shipmentIdToIndexObj: object = {};
      setRows(shipmentRows);
      shipmentRows.forEach((row: ShipmentRow, index: number) => {
        indexToRowsObj[index] = row;
        if (row?.id) {
          shipmentIdToIndexObj[row.id] = index;
        }
      });
      setIndexToRows(indexToRowsObj);
      setShipmentIdToIndex(shipmentIdToIndexObj);
    }
    return () => {
      setRows([]);
      setIndexToRows({});
      setShipmentIdToIndex({});
    };
  }, [combinedShipmentsResponse]);

  useEffect(() => {
    fetchShipments();
  }, [memoizedLimit, memoizedCurrentPage]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    fetchShipments();
  }, [debouncedSortFields]);

  useEffect(() => {
    setCurrentPage(0);
    fetchShipments();
  }, [
    isLate,
    minDate,
    maxDate,
    shipmentDateRangeStart,
    shipmentDateRangeEnd,
    completedDateRangeStart,
    completedDateRangeEnd,
    isOnTime,
    filterSnapshot.debouncedSearchTerm,
    selectedPrimaryFilter,
    selectedCustomFilter,
    selectedDriverIdFilter,
    selectedCustomer,
    trackingNumbersFilter,
    displayIdsFilter,
    orderNameSearch,
    hasOrderInternalNote,
    hasDriverNoteOnly,
    hasWaitTimeOrOverride,
    hasMultipleDriverPayOnly,
    hasUnapprovedDriverPackageReportOnly,
    selectedDriverLabelIds,
    selectedOrderLabelIds,
    selectedEndCustomerLabelIds,
    selectedCourierBrandIds,
    selectedAcquisitionTypes,
    selectedRecurrenceTypes,
    isAttempted,
    selectedPickupZoneIds,
    selectedDropoffZoneIds,
    selectedPackageTypeIds,
    selectedOrderVehicleTypeIds,
    selectedDriverVehicleTypeIds,
    selectedServiceTypeIds,
    minWeightInPounds,
    maxWeightInPounds,
    selectedRouteTemplateIds,
  ]);

  useEffect(() => {
    if (shipmentsApiError || routingJobsError) {
      const error = shipmentsApiError || routingJobsError;
      error && showErrorToast(error, showToast);
      setSortFields([]);
    }
  }, [shipmentsApiError, routingJobsError]);

  const onDispatch = (lastChangedShipmentId?: string | null | undefined) => {
    fetchShipments().then((res) => {
      if (!lastChangedShipmentId) {
        return;
      }
      const data = res.data;
      const shpimentFound = data?.shipments?.edges?.find((shipmentEdge) => {
        return shipmentEdge?.node?.id === lastChangedShipmentId;
      });
      if (shpimentFound && shpimentFound.node?.driverId === null) {
        fetchShipments();
      }
    });
  };

  const fetchShipments = () => {
    if (!employeeId && !courieUser) {
      return Promise.resolve({ data: undefined });
    }
    const shouldUseSalesReport = router.pathname.includes("/office/sales");

    const customFilterVariables = makeGetShipmentsVariable(
      courierId!,
      selectedPrimaryFilter,
      minDate,
      maxDate,
      selectedDriverIdFilter ? [selectedDriverIdFilter] : undefined,
      selectedCustomer,
      selectedCustomFilter,
      shipmentDateRangeStart,
      shipmentDateRangeEnd,
      completedDateRangeStart,
      completedDateRangeEnd
    );
    const adHocFilterVariables = makeShipmentFilterVariables(filterSnapshot, {
      isSalesReport: shouldUseSalesReport,
    });

    const variables = {
      // Strip undefined so an empty ad-hoc filter doesn't clobber values a
      // saved custom filter contributed (e.g. recurrenceTypes, serviceTypeIds).
      // Explicit nulls are preserved (e.g. simpleSearch: null) to keep the
      // original wire contract intact.
      ...omitUndefined(customFilterVariables),
      ...omitUndefined(adHocFilterVariables),
      courierId: courierId!,
      limit: Number(limit.id),
      after: currentPage * Number(limit.id),
      sortFields:
        sortFields && sortFields.length > 0
          ? (sortFields as ShipmentsSorting[])
          : undefined,
      userId: employeeId ? employeeId : courieUser!.id,
    };

    if (shouldUseSalesReport) {
      return getShipmentsForSalesReport({
        variables,
        fetchPolicy: "network-only",
      });
    }

    return getShipments({
      variables,
    });
  };

  return (
    <ShipmentsCoreDataContext.Provider
      value={{
        shipmentsApiResponse: combinedShipmentsResponse as GetShipmentsQuery,
        refetchGetShipments: router.pathname.includes("/office/sales")
          ? refetchShipmentsForSalesReport
          : refetchGetShipments,
        shipmentsApiLoading: isLoading,
        rows,
        onDispatch,
        fetchShipments,
        indexToRows,
        shipmentIdToIndex,
        currentPage,
        setCurrentPage,
        limit,
        setLimit,
        routingJobsData,
        routingJobsLoading,
        shipmentsOverviewCounts,
        refetchRoutingJobs,
      }}
    >
      {children}
    </ShipmentsCoreDataContext.Provider>
  );
};

// the custom hook
const useShipmentsCoreDataContext = () => {
  const context = useContext(ShipmentsCoreDataContext);
  if (context === undefined) {
    throw new Error(
      "useShipmentsCoreDataContext must be used within a ShipmentsCoreDataProvider"
    );
  }
  return context;
};

const useShipmentsCoreDataContextOptional = () => {
  return useContext(ShipmentsCoreDataContext);
};

export {
  ShipmentsCoreDataProvider,
  useShipmentsCoreDataContext,
  useShipmentsCoreDataContextOptional,
};
